import React from 'react';
import { MenuItem, MenuItemProps, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import useModuleAccess from '../../hooks/useModuleAccess';

// `action` is omitted from MenuItemProps because MUI's ButtonBase already uses
// that name for an imperative-handle ref; here it means the permission verb.
interface PermissionMenuItemProps extends Omit<MenuItemProps, 'action'> {
    /** Registry module key the action belongs to, e.g. 'sales', 'payments'. */
    module: string;
    /** Action required, e.g. 'edit', 'create', 'delete'. */
    action: string;
    /** Optional sub-module, when the grant can be scoped finer than the module. */
    subModule?: string | null;
    /** Overrides the default "you don't have permission" tooltip. */
    deniedMessage?: string;
}

/**
 * A MenuItem that stays VISIBLE but goes disabled with a lock icon when the user
 * lacks the permission, so they can see the action exists and ask for access —
 * rather than silently missing it, which is what hiding would do.
 *
 * The click handler is dropped in the denied state so a disabled item can never
 * fire, and the item is wrapped in a span because a disabled MUI MenuItem
 * swallows pointer events and would otherwise never show its tooltip.
 */
export const PermissionMenuItem: React.FC<PermissionMenuItemProps> = ({
    module,
    action,
    subModule = null,
    deniedMessage,
    children,
    ...menuItemProps
}) => {
    const { can } = useModuleAccess();

    if (can(module, action, subModule)) {
        return <MenuItem {...menuItemProps}>{children}</MenuItem>;
    }

    const { onClick, ...allowedProps } = menuItemProps;

    return (
        <Tooltip
            arrow
            placement="left"
            title={deniedMessage || `You do not have permission to ${action} in ${module}`}
        >
            <span style={{ display: 'block' }}>
                <MenuItem {...allowedProps} disabled disableRipple>
                    {children}
                    <LockIcon
                        sx={{
                            fontSize: '15px !important',
                            ml: 'auto',
                            mr: '0 !important',
                            pl: 1,
                            color: 'text.disabled',
                        }}
                    />
                </MenuItem>
            </span>
        </Tooltip>
    );
};

export default PermissionMenuItem;
